import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Auto-categorize glass by description
function categorizeGlass(desc) {
    const d = (desc || '').toLowerCase();
    if (d.includes('טריפלקס') || d.includes('טרפלקס')) return 'טריפלקס';
    if (d.includes('בידודית') || d.includes('בי.') || d.includes('בדודית')) return 'בידודית';
    if (d.includes('רפלקטיבית')) return 'רפלקטיבית';
    if (d.includes('רפפה')) return 'רפפה';
    if (d.includes('מראה')) return 'מראה';
    if (d.includes('פרספקס') || d.includes('פלקליר')) return 'פרספקס / פלקליר';
    if (d.includes('פוליקרבונט')) return 'פוליקרבונט';
    if (d.includes('מחוסמ')) return 'מחוסם';
    if (d.includes('אנטיסן') || d.includes('אנטי סן')) return 'אנטיסן';
    if (d.includes('שקוף') || d.includes('חלבי') || d.includes('משוריין')) return 'שקוף פשוט';
    return 'מיוחד';
}

const CATEGORY_ORDER = ['אחרונים שנבחרו', 'שקוף פשוט', 'מחוסם', 'אנטיסן', 'טריפלקס', 'בידודית', 'רפלקטיבית', 'רפפה', 'מראה', 'פרספקס / פלקליר', 'פוליקרבונט', 'מיוחד'];
const CATEGORY_COLORS = {
    'אחרונים שנבחרו': 'bg-violet-100 text-violet-700 border-violet-200',
    'שקוף פשוט': 'bg-sky-100 text-sky-700 border-sky-200',
    'מחוסם': 'bg-blue-100 text-blue-700 border-blue-200',
    'אנטיסן': 'bg-teal-100 text-teal-700 border-teal-200',
    'טריפלקס': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    'בידודית': 'bg-amber-100 text-amber-700 border-amber-200',
    'רפלקטיבית': 'bg-orange-100 text-orange-700 border-orange-200',
    'רפפה': 'bg-lime-100 text-lime-700 border-lime-200',
    'מראה': 'bg-pink-100 text-pink-700 border-pink-200',
    'פרספקס / פלקליר': 'bg-indigo-100 text-indigo-700 border-indigo-200',
    'פוליקרבונט': 'bg-cyan-100 text-cyan-700 border-cyan-200',
    'מיוחד': 'bg-slate-100 text-slate-700 border-slate-200',
};

export default function GlassOrder() {
    const [width, setWidth] = useState('');
    const [height, setHeight] = useState('');
    const [glassType, setGlassType] = useState(null);
    const [qty, setQty] = useState(1);
    const [items, setItems] = useState([]);
    const [note, setNote] = useState('');

    // Glass data from DB
    const [allGlass, setAllGlass] = useState([]);
    const [glassLoading, setGlassLoading] = useState(true);

    // Popup state
    const [showPicker, setShowPicker] = useState(false);
    const [pickerSearch, setPickerSearch] = useState('');
    const [pickerCategory, setPickerCategory] = useState('all');
    const [pickerTarget, setPickerTarget] = useState(null); // null = top form, or item id for table row
    const searchRef = useRef(null);

    // Recently selected (session-only)
    const [recentIds, setRecentIds] = useState([]);

    // Session price overrides (not saved to DB)
    const [sessionPrices, setSessionPrices] = useState({}); // { glassId: overriddenPrice }
    const [editingRowPrice, setEditingRowPrice] = useState(null);
    const [rowPriceValue, setRowPriceValue] = useState('');

    // Fetch glass types from GlassCatalog DB
    useEffect(() => {
        const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
        fetch(`${API_BASE}/api/glass-catalog`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
            .then(r => r.json())
            .then(data => {
                // Map from GlassCatalog format → picker format
                const glasses = (Array.isArray(data) ? data : []).map(g => ({
                    ...g,
                    id: g.glassId,       // GlassOrder uses .id
                }));
                setAllGlass(glasses);
                if (glasses.length > 0 && !glassType) setGlassType(glasses[0]);
                setGlassLoading(false);
            })
            .catch(() => setGlassLoading(false));
    }, []); // eslint-disable-line

    // Group glass by category
    const glassByCategory = useMemo(() => {
        const groups = {};
        for (const g of allGlass) {
            const cat = categorizeGlass(g.description);
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(g);
        }
        // Sort each group by description
        for (const cat of Object.keys(groups)) {
            groups[cat].sort((a, b) => a.description.localeCompare(b.description));
        }
        return groups;
    }, [allGlass]);

    // Recently selected glasses
    const recentGlasses = useMemo(() => {
        return recentIds.map(id => allGlass.find(g => g.id === id)).filter(Boolean);
    }, [recentIds, allGlass]);

    // Filtered glasses for picker
    const filteredPickerGlasses = useMemo(() => {
        let glasses;
        if (pickerCategory === 'all') {
            glasses = allGlass;
        } else if (pickerCategory === 'אחרונים שנבחרו') {
            glasses = recentGlasses;
        } else {
            glasses = glassByCategory[pickerCategory] || [];
        }
        if (pickerSearch) {
            const q = pickerSearch.toLowerCase();
            glasses = glasses.filter(g => g.description.toLowerCase().includes(q) || String(g.id).includes(q));
        }
        return glasses;
    }, [allGlass, pickerCategory, pickerSearch, glassByCategory, recentGlasses]);

    // Open picker
    const openPicker = (target = null) => {
        setPickerTarget(target);
        setPickerSearch('');
        setPickerCategory(recentIds.length > 0 ? 'אחרונים שנבחרו' : 'all');
        setShowPicker(true);
        setTimeout(() => searchRef.current?.focus(), 100);
    };

    // Select glass from picker
    const selectGlass = (glass) => {
        // Add to recents
        setRecentIds(prev => {
            const filtered = prev.filter(id => id !== glass.id);
            return [glass.id, ...filtered].slice(0, 10);
        });

        if (pickerTarget === null) {
            // Top form selection
            setGlassType(glass);
        } else {
            // Table row change
            setItems(prev => prev.map(item =>
                item.id === pickerTarget ? { ...item, glass } : item
            ));
        }
        setShowPicker(false);
    };

    const handleAdd = () => {
        const w = parseFloat(width);
        const h = parseFloat(height);
        if (!w || !h || w <= 0 || h <= 0 || !glassType) return;

        setItems(prev => [...prev, {
            id: Date.now(),
            width: w,
            height: h,
            glass: glassType,
            qty: parseInt(qty) || 1,
        }]);
        setWidth('');
        setHeight('');
        setQty(1);
    };

    const handleRemove = (id) => setItems(prev => prev.filter(item => item.id !== id));
    const handleQtyChange = (id, newQty) => setItems(prev => prev.map(item => item.id === id ? { ...item, qty: parseInt(newQty) || 1 } : item));

    // Get effective price: session override > DB price
    const getItemPrice = (item) => {
        if (sessionPrices[item.glass.id] !== undefined) return sessionPrices[item.glass.id];
        return item.glass.priceSqM || 0;
    };

    // Save price: session only or to DB
    const handlePriceSave = async (item, saveToDB = false) => {
        const price = parseFloat(rowPriceValue) || 0;
        if (saveToDB && item.glass._id) {
            try {
                const token = localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
                const res = await fetch(`${API_BASE}/api/glass-catalog/${item.glass._id}/price`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    body: JSON.stringify({ priceSqM: price })
                });
                if (!res.ok) throw new Error('שגיאה בשמירה');
                // Update glass in all items
                setItems(prev => prev.map(it => it.glass.id === item.glass.id ? { ...it, glass: { ...it.glass, priceSqM: price } } : it));
                // Also update allGlass
                setAllGlass(prev => prev.map(g => g.id === item.glass.id ? { ...g, priceSqM: price } : g));
                // Remove session override since DB is now updated
                setSessionPrices(prev => { const n = { ...prev }; delete n[item.glass.id]; return n; });
            } catch (err) { alert(err.message); }
        } else {
            // Session-only override
            setSessionPrices(prev => ({ ...prev, [item.glass.id]: price }));
        }
        setEditingRowPrice(null);
    };

    const totalPieces = items.reduce((sum, item) => sum + item.qty, 0);

    const handleWhatsApp = () => {
        const dateStr = new Date().toLocaleDateString('he-IL');
        let msg = `*הזמנת זכוכית — אלום נואטחה*\n`;
        msg += `תאריך: ${dateStr}\n`;
        if (note) msg += `הערות: ${note}\n`;
        msg += `───────────\n`;
        items.forEach((item, i) => {
            msg += `${i + 1}. ${item.glass.description}\n`;
            msg += `   ${item.width} × ${item.height} מ"מ | כמות: ${item.qty}\n`;
        });
        msg += `───────────\n`;
        msg += `*סה"כ: ${items.length} שורות | ${totalPieces} יחידות*`;
        const encoded = encodeURIComponent(msg);
        window.location.href = `whatsapp://send?text=${encoded}`;
    };

    if (glassLoading) {
        return (
            <div className="max-w-5xl mx-auto p-6 mt-10 text-center" dir="rtl">
                <div className="inline-block w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-slate-500 mt-4 font-bold">טוען סוגי זכוכית...</p>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto p-6 bg-gray-50 rounded-xl shadow-lg mt-10 print:shadow-none print:bg-white print:mt-0 print:p-0" dir="rtl">

            <Link to="/professionals" className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-700 mb-4 transition-colors font-bold print:hidden">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                חזרה למחשבונים
            </Link>

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-blue-900 print:text-black">הזמנת זכוכית</h2>
                {items.length > 0 && (
                    <div className="flex gap-3 print:hidden">
                        <button onClick={() => window.print()} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-bold transition-colors shadow-md">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                            הדפס
                        </button>
                        <button onClick={handleWhatsApp} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-bold transition-colors shadow-md">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                            שלח PDF
                        </button>
                    </div>
                )}
            </div>

            {/* Input Form */}
            <div className="bg-white p-6 rounded-lg shadow-sm border mb-6 print:hidden">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
                    <div className="col-span-2 md:col-span-1">
                        <label className="block text-gray-700 font-bold mb-2">סוג זכוכית:</label>
                        <button
                            onClick={() => openPicker(null)}
                            className="w-full border-2 border-blue-200 hover:border-blue-400 p-2 rounded-lg bg-white cursor-pointer text-right transition-colors flex items-center justify-between gap-2 min-h-[42px]"
                        >
                            <span className="truncate text-sm font-medium">{glassType?.description || 'בחר סוג...'}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                        </button>
                        {glassType?.priceSqM > 0 && (
                            <div className="text-[10px] text-emerald-600 font-bold mt-1">{glassType.priceSqM} ₪/מ"ר</div>
                        )}
                    </div>
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">רוחב (מ"מ):</label>
                        <input type="number" value={width} onChange={(e) => setWidth(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none" placeholder="800" />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">גובה (מ"מ):</label>
                        <input type="number" value={height} onChange={(e) => setHeight(e.target.value)} className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none" placeholder="1200" />
                    </div>
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">כמות:</label>
                        <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} min="1" className="border-2 border-blue-200 p-2 rounded-lg w-full outline-none" />
                    </div>
                    <div>
                        <button onClick={handleAdd} className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold transition-colors shadow-md flex items-center justify-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                            הוסף
                        </button>
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-gray-700 font-bold mb-2">הערות להזמנה:</label>
                    <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className="border-2 border-gray-200 p-2 rounded-lg w-full outline-none" placeholder="שם לקוח, כתובת, או הערה כללית..." />
                </div>
            </div>

            {/* Print Header */}
            <div className="hidden print:block mb-4 pb-4" style={{ borderBottom: '3px solid #1e3a5f' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <img src="/assets/logo-circle.png" alt="Alum Nawatha" style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                        <div>
                            <h1 style={{ fontSize: '24pt', fontWeight: 'bold', color: '#1e3a5f', margin: 0, lineHeight: 1.2 }}>אלום נואטחה בע"מ</h1>
                            <p style={{ fontSize: '10pt', color: '#555', margin: '2px 0 0 0', letterSpacing: '2px' }}>ALUM NAWATHA LTD</p>
                            <p style={{ fontSize: '8pt', color: '#888', margin: '2px 0 0 0' }}>מערכות אלומיניום מתקדמות | טל: 052-878-4031</p>
                        </div>
                    </div>
                    <table style={{ fontSize: '9pt', borderCollapse: 'collapse', textAlign: 'right', direction: 'rtl' }}>
                        <tbody>
                            <tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>תאריך:</td><td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{new Date().toLocaleDateString('he-IL')}</td></tr>
                            <tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333', borderBottom: '1px solid #ddd' }}>סה"כ פריטים:</td><td style={{ padding: '3px 12px', color: '#555', borderBottom: '1px solid #ddd' }}>{totalPieces} יח'</td></tr>
                            {note && (<tr><td style={{ padding: '3px 12px', fontWeight: 'bold', color: '#333' }}>הערות:</td><td style={{ padding: '3px 12px', color: '#555' }}>{note}</td></tr>)}
                        </tbody>
                    </table>
                </div>
                <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '13pt', fontWeight: 'bold', color: '#1e3a5f' }}>הזמנת זכוכית</div>
            </div>

            {/* Items Table */}
            {items.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden print:border-none print:shadow-none">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right border-collapse">
                            <thead>
                                <tr className="bg-blue-100 border-b-2 border-blue-200 text-blue-900 print:bg-gray-100 print:border-gray-400 print:text-black">
                                    <th className="p-3 text-center border-l print:border-gray-300 w-12">#</th>
                                    <th className="p-3 border-l print:border-gray-300">סוג זכוכית</th>
                                    <th className="p-3 border-l print:border-gray-300 w-20">מחיר/מ"ר</th>
                                    <th className="p-3 border-l print:border-gray-300">רוחב (מ"מ)</th>
                                    <th className="p-3 border-l print:border-gray-300">גובה (מ"מ)</th>
                                    <th className="p-3 border-l print:border-gray-300">כמות</th>
                                    <th className="p-3 print:hidden">פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50 print:border-gray-300">
                                        <td className="p-3 text-center font-bold text-gray-500 border-l print:border-gray-300">{index + 1}</td>
                                        <td className="p-3 border-l print:border-gray-300">
                                            <button
                                                onClick={() => openPicker(item.id)}
                                                className="text-right w-full bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-300 p-1.5 rounded-lg cursor-pointer transition-colors text-sm font-medium print:hidden truncate flex items-center justify-between gap-1"
                                            >
                                                <span className="truncate">{item.glass.description}</span>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                                            </button>
                                            <span className="hidden print:inline font-bold">{item.glass.description}</span>
                                        </td>
                                        <td className="p-3 text-center border-l print:border-gray-300 print:text-black">
                                            {editingRowPrice === item.id ? (
                                                <div className="flex items-center gap-1 justify-center">
                                                    <input type="number" step="0.01" value={rowPriceValue} onChange={e => setRowPriceValue(e.target.value)}
                                                        className="w-16 px-1 py-0.5 text-xs border border-blue-300 rounded focus:ring-1 focus:ring-blue-400 outline-none font-mono text-center" autoFocus
                                                        onKeyDown={e => { if (e.key === 'Enter') { handlePriceSave(item); } if (e.key === 'Escape') setEditingRowPrice(null); }} />
                                                    <div className="flex flex-col gap-0.5">
                                                        <button onClick={() => handlePriceSave(item, false)} className="text-[9px] bg-blue-500 text-white px-1 py-0.5 rounded font-bold" title="שמור להזמנה זו בלבד">הזמנה</button>
                                                        <button onClick={() => handlePriceSave(item, true)} className="text-[9px] bg-emerald-500 text-white px-1 py-0.5 rounded font-bold" title="שמור לצמיתות ב-DB">DB</button>
                                                    </div>
                                                    <button onClick={() => setEditingRowPrice(null)} className="text-slate-400 text-xs">✕</button>
                                                </div>
                                            ) : (
                                                <div className="cursor-pointer group/p" onClick={() => { setEditingRowPrice(item.id); setRowPriceValue(getItemPrice(item)); }}>
                                                    {getItemPrice(item) > 0 ? (
                                                        <span className={`font-mono text-sm font-bold ${sessionPrices[item.glass.id] !== undefined ? 'text-blue-600' : 'text-emerald-600'}`}>
                                                            {getItemPrice(item)}₪
                                                            {sessionPrices[item.glass.id] !== undefined && <span className="text-[8px] text-blue-400 block">מחיר הזמנה</span>}
                                                        </span>
                                                    ) : (
                                                        <span className="text-red-400 text-xs">—</span>
                                                    )}
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-slate-300 opacity-0 group-hover/p:opacity-100 transition-opacity inline mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-3 font-mono text-lg text-red-600 font-bold border-l print:border-gray-300 print:text-black">{item.width}</td>
                                        <td className="p-3 font-mono text-lg text-red-600 font-bold border-l print:border-gray-300 print:text-black">{item.height}</td>
                                        <td className="p-3 border-l print:border-gray-300">
                                            <input type="number" value={item.qty} onChange={(e) => handleQtyChange(item.id, e.target.value)} min="1" className="border border-gray-200 p-1 rounded w-16 text-center outline-none print:hidden" />
                                            <span className="hidden print:inline font-bold">{item.qty}</span>
                                        </td>
                                        <td className="p-3 print:hidden">
                                            <button onClick={() => handleRemove(item.id)} className="text-red-500 hover:text-red-700 font-bold transition-colors">✕ הסר</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-gray-800 text-white p-4 flex justify-between items-center rounded-b-lg print:bg-gray-100 print:text-black print:border-t-2 print:border-gray-400">
                        <h3 className="text-lg font-bold">סה"כ:</h3>
                        <div className="text-xl font-bold">{items.length} שורות | {totalPieces} יחידות</div>
                    </div>
                </div>
            )}

            {items.length === 0 && (
                <div className="text-center py-16 text-gray-400 print:hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                    <p className="text-lg">הרשימה ריקה — הוסף פריטים למעלה</p>
                </div>
            )}

            {/* ======= Glass Picker Modal ======= */}
            {showPicker && (
                <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPicker(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl overflow-hidden flex flex-col" dir="rtl" onClick={e => e.stopPropagation()}>

                        {/* Header */}
                        <div className="bg-blue-50 border-b border-blue-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
                            <div>
                                <h3 className="font-black text-slate-800 text-lg">בחירת סוג זכוכית</h3>
                                <p className="text-sm text-slate-500">{allGlass.length} סוגים זמינים</p>
                            </div>
                            <button onClick={() => setShowPicker(false)} className="text-slate-400 hover:text-slate-600 p-1">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Search */}
                        <div className="px-6 pt-4 pb-2 flex-shrink-0">
                            <div className="relative">
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                </span>
                                <input
                                    ref={searchRef}
                                    type="text"
                                    placeholder="חיפוש לפי שם, עובי, או קוד..."
                                    value={pickerSearch}
                                    onChange={(e) => setPickerSearch(e.target.value)}
                                    className="w-full pl-4 pr-10 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all font-medium"
                                />
                            </div>
                        </div>

                        {/* Category Chips */}
                        <div className="px-6 py-2 flex flex-wrap gap-2 flex-shrink-0 border-b border-slate-100">
                            <button
                                onClick={() => setPickerCategory('all')}
                                className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${pickerCategory === 'all' ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                            >הכל ({allGlass.length})</button>
                            {CATEGORY_ORDER.filter(cat => cat === 'אחרונים שנבחרו' ? recentIds.length > 0 : (glassByCategory[cat]?.length > 0)).map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setPickerCategory(cat)}
                                    className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${pickerCategory === cat
                                        ? 'bg-slate-800 text-white border-slate-800'
                                        : (CATEGORY_COLORS[cat] || 'bg-slate-100 text-slate-600 border-slate-200') + ' hover:opacity-80'
                                    }`}
                                >
                                    {cat === 'אחרונים שנבחרו' ? `⏱ אחרונים (${recentIds.length})` : `${cat} (${glassByCategory[cat]?.length || 0})`}
                                </button>
                            ))}
                        </div>

                        {/* Glass List */}
                        <div className="flex-1 overflow-y-auto px-4 py-3">
                            {filteredPickerGlasses.length === 0 ? (
                                <div className="text-center py-10 text-slate-400">
                                    <p className="font-bold">לא נמצאו תוצאות</p>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {filteredPickerGlasses.map(g => {
                                        const isRecent = recentIds.includes(g.id);
                                        return (
                                            <button
                                                key={g.id}
                                                onClick={() => selectGlass(g)}
                                                className="w-full text-right p-3 rounded-xl hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-all flex items-center justify-between gap-3 group"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        {isRecent && <span className="text-[9px] bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full font-bold">אחרון</span>}
                                                        <span className="font-bold text-slate-800 text-sm truncate">{g.description}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs text-slate-400">
                                                        <span>קוד: {g.id}</span>
                                                        {g.thickness && <span>עובי: {g.thickness} מ"מ</span>}
                                                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${CATEGORY_COLORS[categorizeGlass(g.description)] || 'bg-slate-100 text-slate-500'}`}>
                                                            {categorizeGlass(g.description)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-left flex-shrink-0">
                                                    {g.priceSqM > 0 ? (
                                                        <span className="font-black text-emerald-600 text-sm">{g.priceSqM} ₪/מ"ר</span>
                                                    ) : (
                                                        <span className="text-xs text-red-400">ללא מחיר</span>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
